import React from 'react';
import { seoTools } from '@/data/seoTools';
import { motion } from 'framer-motion';
import { SEO } from '@/components/SEO';
import { BrainCircuit, Layers, BookOpen, PieChart, ChevronRight, ArrowLeft, GraduationCap, UploadCloud, Sparkles } from 'lucide-react';

export const ToolsPage = ({ onNavigate }: { onNavigate: (path: string) => void }) => {
  const getIcon = (name: string) => {
    switch (name) {
      case 'GraduationCap': return <GraduationCap className="w-8 h-8 text-emerald-400" />;
      case 'UploadCloud': return <UploadCloud className="w-8 h-8 text-teal-400" />;
      case 'BrainCircuit': return <BrainCircuit className="w-8 h-8 text-indigo-400" />;
      case 'Layers': return <Layers className="w-8 h-8 text-purple-400" />;
      case 'BookOpen': return <BookOpen className="w-8 h-8 text-amber-400" />;
      case 'PieChart': return <PieChart className="w-8 h-8 text-orange-400" />;
      default: return <Sparkles className="w-8 h-8 text-indigo-400" />;
    }
  };

  return (
    <>
      <SEO 
        title="Free AI Educational Tools | Professor X AI"
        description="Access free educational utility tools including AI Doubt Solver, OneShot Learning Masterclass, Study From Your Materials analyzer, and Smart Notes generators."
        canonical="https://professorxai.info/tools"
      />
      <div className="max-w-[1700px] mx-auto px-6 py-12 text-slate-200">
        <button 
          onClick={() => onNavigate('/')}
          className="group text-slate-400 hover:text-white flex items-center gap-2 font-medium mb-12 transition-colors relative z-10"
        >
          <div className="p-2 rounded-full bg-slate-800/50 group-hover:bg-slate-800 transition-colors border border-white/5">
            <ArrowLeft className="w-4 h-4" />
          </div>
          Back to Home
        </button>

        <h1 className="text-5xl font-black text-white mb-4 text-center">Educational Tools</h1>
        <p className="text-slate-400 text-center mb-6 max-w-2xl mx-auto">Boost your preparation with our AI-powered study utilities, OneShot masterclass engines, and notes generators.</p>
        
        <div className="mb-12 flex justify-center">
          <div className="w-24 h-24 md:w-32 md:h-32 rounded-full border-2 border-indigo-500/30 p-1 bg-gradient-to-br from-indigo-500/20 to-purple-500/20 shadow-[0_0_30px_rgba(79,70,229,0.2)] mx-auto relative z-10">
            <img src="/profxlogo.png" alt="Professor X AI" className="w-full h-full rounded-full object-cover bg-black" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {seoTools.map((tool, idx) => {
            const isComingSoon = ['mock-test-practice', 'rank-predictor'].includes(tool.slug);
            return (
            <motion.div 
              key={tool.slug}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: idx * 0.08 }}
              onClick={() => { if(!isComingSoon) onNavigate('/'); }}
              className="bg-gradient-to-br from-slate-900/80 to-[#0a0a0f] border border-white/10 rounded-3xl p-7 cursor-pointer hover:border-indigo-500/40 hover:shadow-[0_0_30px_rgba(79,70,229,0.2)] transition-all group flex flex-col justify-between"
            >
              <div>
                <div className="p-4 rounded-2xl bg-white/5 border border-white/10 group-hover:bg-white/10 transition-colors w-fit mb-5">
                  {getIcon(tool.icon)}
                </div>
                <h2 className="text-2xl font-extrabold text-white mb-2 tracking-tight">{tool.name}</h2>
                <p className="text-slate-400 leading-relaxed mb-6 text-sm">{tool.description}</p>
              </div>
              <div className="flex items-center text-sm font-bold text-indigo-400 pt-2 border-t border-white/5">
                {isComingSoon ? (
                  <span className="text-slate-500 flex items-center">Coming Soon</span>
                ) : (
                  <span className="flex items-center text-emerald-400 group-hover:text-emerald-300">Launch Tool <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" /></span>
                )}
              </div>
            </motion.div>
            )
          })}
        </div>
      </div>
    </>
  );
};
